// Shared premium-protein pricing logic — used by ProteinSubstitutionSection
// (per-option "+$X" badge) and MealModal (top-of-modal total price) so both
// surfaces agree on when a premium protein actually incurs a charge.
import { groupDatesByWeek } from './deliveryDates';

// Does a premium protein on THIS dish cost the customer extra, given the week's
// premium-protein allowance from their subscription?
//
// The answer is deliberately independent of which protein is currently selected
// — picking a premium protein must not change it. (It used to: selecting one
// made every "+$X" badge on the dish disappear and dropped the surcharge from
// the modal's total, even though the customer was being charged for it.)
//
// proteinOption: the option being evaluated
// proteinOptions: the full list of protein options for this dish (to find the currently active one)
// mealType: order.meal — the premium allowance is tracked per meal type
// billingChanges/deliveryDates/selectedDeliveryDate: meal-plan-app subscription billing
//   context. When absent (e.g. storefront guest checkout), any priced option is always charged.
// countedQuantity: servings of this dish already reflected in billingChanges' weekly
//   premium totals — 0 for an uncommitted draft, which isn't in `orders` yet.
export const shouldChargePremiumProtein = ({
    proteinOption,
    proteinOptions,
    mealType,
    billingChanges,
    deliveryDates,
    selectedDeliveryDate,
    countedQuantity = 0,
}) => {
    const hasUpgradePrice = proteinOption?.priceSubscription != null && Number(proteinOption.priceSubscription) > 0;
    if (!hasUpgradePrice) {
        return false;
    }

    if (!billingChanges?.premiumUsageByWeek || !deliveryDates || !selectedDeliveryDate) {
        return true;
    }

    const datesByWeek = groupDatesByWeek(deliveryDates);
    let weekKey = null;
    for (const [key, datesInWeek] of Object.entries(datesByWeek)) {
        if (datesInWeek.includes(selectedDeliveryDate)) {
            weekKey = key;
            break;
        }
    }

    if (!weekKey) {
        return true;
    }

    const premiumUsage = billingChanges.premiumUsageByWeek[weekKey]?.[mealType];
    if (!premiumUsage) {
        return true;
    }

    // `ordered` already includes this dish's own servings when its active protein
    // is premium. Take them back out to get the rest of the week, then add this
    // dish in unconditionally — that's what makes the result the same before and
    // after a premium protein is picked here.
    const activeProtein = (proteinOptions || []).find(opt => opt.isActive);
    const activeIsPremium = Number(activeProtein?.priceSubscription) > 0;
    const orderedElsewhere = Math.max(
        0,
        (premiumUsage.ordered || 0) - (activeIsPremium ? countedQuantity : 0)
    );
    const servings = Math.max(countedQuantity, 1);

    return orderedElsewhere + servings > (premiumUsage.subscribed || 0);
};

// Which field represents this option's upgrade cost depends on the surface:
// meal-plan subscription uses `priceSubscription` (flat premium surcharge, e.g. $4); storefront
// guest checkout uses `priceALaCarte` (the real per-item upcharge for individual sale).
export const getProteinUpgradeAmount = (proteinOption, useALaCarte = false) =>
    useALaCarte ? Number(proteinOption?.priceALaCarte) || 0 : Number(proteinOption?.priceSubscription) || 0;

// Whether selecting/holding this option actually incurs a charge right now.
// Storefront (useALaCarte): no subscription allowance to track — any priced option always charges.
// Meal-plan subscription: delegate to the premium-usage-aware logic above.
export const isProteinUpgradeChargeable = (params) => {
    if (params?.useALaCarte) {
        return getProteinUpgradeAmount(params.proteinOption, true) > 0;
    }
    return shouldChargePremiumProtein(params || {});
};
